import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { updateNetSuiteAsset } from 'store/netSuiteAssetManager'
import { EditLicenseModal } from './EditLicenseModal'
import { NetSuiteAsset, SchoolLookUpSiteData, SiteAddress } from 'types'
import { useNetSuiteLookup } from './hooks/useNetSuiteLookup'
import { useCustomerIdValidation } from './hooks/useCustomerIdValidation'
import { useSiteChangeDetection } from './hooks/useSiteChangeDetection'

interface EditLicenseModalContainerProps {
  isOpen: boolean
  netSuiteAsset?: NetSuiteAsset
  netSuiteAssetId?: number
  hasInactiveNetSuiteCustomer?: boolean
  toggle: () => void
}

// Constants for form field names - now flat structure
const SITE_FIELDS = {
  CITY: 'city',
  COMPANY_NAME: 'companyName',
  COUNTRY: 'country',
  NETSUITE_CUSTOMER_ID: 'netsuiteCustomerId',
  NS_CUSTOMER_ID: 'nsCustomerId',
  REGION: 'region',
  ORGANIZATION_NAME: 'organizationName',
} as const

const EditLicenseModalContainer: React.FC<EditLicenseModalContainerProps> = (
  props: EditLicenseModalContainerProps,
) => {
  const dispatch = useDispatch()

  const { netSuiteAsset, isOpen } = props || {}

  // Memoize initial school data to avoid recalculation on every render
  const initialSchoolData = useMemo<SchoolLookUpSiteData>(() => {
    const { site } = netSuiteAsset || ({} as NetSuiteAsset)
    const address = site?.address || ({} as SiteAddress)

    return {
      city: address.city || '',
      companyName: site?.name || '',
      country: address.country || '',
      netsuiteCustomerId: null, // Will be looked up by nsCustomerId
      nsCustomerId: site?.nsCustomerId || '',
      organizationName: site?.name || '',
      region: address.region || '',
    }
  }, [netSuiteAsset])

  // Store the initial site data for comparison - use useRef to make it truly static
  const initialSiteDataRef = useRef<Partial<SchoolLookUpSiteData>>(null)

  // Only set the initial site data once when the modal opens - no dependencies
  if (!initialSiteDataRef.current && netSuiteAsset) {
    const { site } = netSuiteAsset || ({} as NetSuiteAsset)
    const address = site?.address || ({} as SiteAddress)

    initialSiteDataRef.current = {
      nsCustomerId: site?.nsCustomerId || '',
      netsuiteCustomerId: null, // Will be looked up by nsCustomerId
      organizationName: site?.name || '',
      companyName: site?.name || '',
      city: address.city || '',
      country: address.country || '',
      region: address.region || '',
    }
  }

  const initialSiteData = initialSiteDataRef.current || {}

  // Consolidated state management
  const [currentSchool, setCurrentSchool] =
    useState<SchoolLookUpSiteData>(initialSchoolData)
  const [isInitialLoad, setIsInitialLoad] = useState(true)

  // Track if component is mounted to prevent state updates after unmount
  const isMountedRef = useRef(true)

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  // Update school data when initial data changes
  useEffect(() => {
    setCurrentSchool(initialSchoolData)
  }, [initialSchoolData])

  // Lookup NetSuite customer on initial load using hook
  const handleLookupComplete = useCallback(
    (schoolData: SchoolLookUpSiteData) => {
      // Check if component is still mounted before updating state
      if (!isMountedRef.current) return
      setCurrentSchool(schoolData)
      // Update the initial site data to reflect the looked-up NetSuite customer
      initialSiteDataRef.current = {
        ...initialSiteDataRef.current,
        netsuiteCustomerId: schoolData.netsuiteCustomerId,
        companyName: schoolData.companyName,
        organizationName: schoolData.organizationName,
      }
      setIsInitialLoad(false)
    },
    [],
  )

  useNetSuiteLookup({
    nsCustomerId: netSuiteAsset?.site?.nsCustomerId,
    onLookupComplete: handleLookupComplete,
    enabled: !!netSuiteAsset && isOpen,
  })

  // Handle case when no nsCustomerId exists
  useEffect(() => {
    if (
      netSuiteAsset &&
      isOpen &&
      !netSuiteAsset.site?.nsCustomerId &&
      isInitialLoad
    ) {
      setIsInitialLoad(false)
    }
  }, [netSuiteAsset, isOpen, isInitialLoad])

  // Use shared site change detection
  const { checkSiteChanged } = useSiteChangeDetection({
    initialSiteData,
    isInitialLoad,
  })

  // Helper function for setting form field values
  const setValue = useCallback(
    async (formik: any, fieldName: string, value: any, validate = true) => {
      await formik.setFieldValue(fieldName, value, validate)
    },
    [],
  )

  // Optimized batch field updates to reduce re-renders and improve performance
  const updateSiteFields = useCallback(
    async (formik: any, siteData: SchoolLookUpSiteData) => {
      const currentValues = formik.values

      // Define all possible field updates with their values
      const allFieldUpdates = [
        [SITE_FIELDS.CITY, siteData.city],
        [SITE_FIELDS.COMPANY_NAME, siteData.companyName],
        [SITE_FIELDS.COUNTRY, siteData.country],
        [SITE_FIELDS.NETSUITE_CUSTOMER_ID, siteData.netsuiteCustomerId ?? null],
        [SITE_FIELDS.NS_CUSTOMER_ID, ''], // Clear nsCustomerId when selecting from dropdown
        [SITE_FIELDS.REGION, siteData.region],
        [SITE_FIELDS.ORGANIZATION_NAME, siteData.organizationName],
      ]

      // Filter to only fields that have actually changed
      const fieldUpdates = allFieldUpdates.filter(([field, newValue]) => {
        const currentValue = currentValues[field] || ''
        return currentValue !== newValue
      })

      // Only update fields that have changed
      if (fieldUpdates.length > 0) {
        await Promise.all(
          fieldUpdates.map(([field, value]) =>
            setValue(formik, field as string, value),
          ),
        )
      }

      // Check if site has changed from initial values
      const siteChanged = checkSiteChanged(siteData)
      const currentSiteWasChanged = formik.values.siteWasChanged

      // Only update if the state actually changed to prevent flickering
      if (siteChanged !== currentSiteWasChanged) {
        formik.setFieldValue('siteWasChanged', siteChanged)
        if (siteChanged) {
          formik.setFieldValue('siteChangeChecked', false)
        }
      }
    },
    [setValue, checkSiteChanged],
  )

  const schoolSelected = useCallback(
    async (formik: any, site: SchoolLookUpSiteData) => {
      await updateSiteFields(formik, site)
    },
    [updateSiteFields],
  )

  // Handle valid customer from validation
  const handleValidCustomer = useCallback(
    async (schoolData: SchoolLookUpSiteData, formik: any) => {
      // When entering nsCustomerId manually, keep both nsCustomerId and netsuiteCustomerId
      // so the dropdown shows the selected site
      const fieldUpdates = [
        [SITE_FIELDS.CITY, schoolData.city],
        [SITE_FIELDS.COMPANY_NAME, schoolData.companyName],
        [SITE_FIELDS.COUNTRY, schoolData.country],
        [SITE_FIELDS.NETSUITE_CUSTOMER_ID, schoolData.netsuiteCustomerId], // Keep netsuiteCustomerId to show in dropdown
        [SITE_FIELDS.NS_CUSTOMER_ID, schoolData.nsCustomerId], // Keep the entered nsCustomerId
        [SITE_FIELDS.REGION, schoolData.region],
        [SITE_FIELDS.ORGANIZATION_NAME, schoolData.organizationName],
      ]

      // Batch all field updates to minimize re-renders
      await Promise.all(
        fieldUpdates.map(([field, value]) =>
          setValue(formik, field as string, value),
        ),
      )

      setCurrentSchool(schoolData)

      // Update the initial site data to reflect the looked-up NetSuite customer
      initialSiteDataRef.current = {
        ...initialSiteDataRef.current,
        netsuiteCustomerId: schoolData.netsuiteCustomerId,
        companyName: schoolData.companyName,
        organizationName: schoolData.organizationName,
        nsCustomerId: schoolData.nsCustomerId,
      }
    },
    [setValue],
  )

  // Handle clearing customer ID
  const handleClearCustomer = useCallback(
    async (formik: any, currentSchool: SchoolLookUpSiteData) => {
      const schoolData: SchoolLookUpSiteData = {
        city: currentSchool.city,
        companyName: currentSchool.companyName,
        country: currentSchool.country,
        netsuiteCustomerId: currentSchool.netsuiteCustomerId,
        nsCustomerId: '',
        organizationName: currentSchool.organizationName,
        region: currentSchool.region,
      }
      await updateSiteFields(formik, schoolData)
      setCurrentSchool(schoolData)
    },
    [updateSiteFields],
  )

  // Use shared customer ID validation hook
  const { validateAsync: customerIdValidateAsync } = useCustomerIdValidation({
    onValidCustomer: handleValidCustomer,
    onClear: handleClearCustomer,
    currentSchool,
  })

  // Handle form field changes
  const handleChange = useCallback(
    (formik: any, e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target
      formik.setFieldValue(name, value, true)
    },
    [],
  )

  // Handle form submission with optimized payload structure
  const handleSubmit = useCallback(
    async (values: any, { setSubmitting }: any) => {
      const {
        selectedNetSuiteAsset,
        nsCustomerId,
        country,
        region,
        city,
        companyName,
        organizationName,
      } = values
      // Transform flat form data back to nested structure for API
      const requestPayload = {
        adminToAssign: selectedNetSuiteAsset.adminEmail,
        city: city,
        country: country,
        siteName: companyName || organizationName,
        region: region,
        netSuiteAssetId: selectedNetSuiteAsset.id,
        nsCustomerId: nsCustomerId,
        isTosConfirmed: true,
      }

      await dispatch(
        updateNetSuiteAsset(selectedNetSuiteAsset.id, requestPayload),
      )
      // Check if component is still mounted before updating state
      if (!isMountedRef.current) return

      // Always close modal and reset state - notifications (success/error) are shown globally
      props.toggle()
      setCurrentSchool(initialSchoolData)
      setIsInitialLoad(true)
      setSubmitting(false)
    },
    [dispatch, props, initialSchoolData],
  )

  // Validate school lookup form fields
  const schoolLookupIsValid = useCallback((formikProps: any) => {
    const { nsCustomerId, netsuiteCustomerId, organizationName } =
      formikProps.values
    return nsCustomerId || netsuiteCustomerId || organizationName
  }, [])

  // Memoized container props to prevent unnecessary re-renders of the presentation component
  const containerProps = useMemo(
    () => ({
      ...props,
      currentSchool,
      customerIdValidateAsync,
      handleChange,
      handleSubmit,
      initialSiteData,
      isInitialLoad,
      schoolLookupIsValid,
      schoolSelected,
      selectedNetSuiteAsset: netSuiteAsset,
      selectedSites: [],
      selectedSitesUsers: [],
      setValue,
    }),
    [
      currentSchool,
      customerIdValidateAsync,
      handleChange,
      handleSubmit,
      initialSiteData,
      isInitialLoad,
      netSuiteAsset,
      props,
      schoolLookupIsValid,
      schoolSelected,
      setValue,
    ],
  )

  return <EditLicenseModal {...containerProps} />
}

export default EditLicenseModalContainer
